import IRepository from "data/IRepository";
import { isSuccessStatus } from "data/helpers/responseHelper";
import DataQuery from "data/DataQuery";
import { Contracting_Approve, Contracting_Cancel, Contracting_Edit, Contracting_List, Contracting_Note } from "data/endpoints";
import { useQuery } from "data/hooks/useQuery";
import ActionResultDTO from "features/shared/dtos/ActionResultDTO";
import PagedDataResultDTO from "features/shared/dtos/PagedDataResultDTO";
import ContractingSimplifiedDTO from "features/contracting/dtos/ContractingSimplifiedDTO";
import AlertWithButton from "features/shared/alerts/components/AlertWithButton";
import { AlertType } from "features/shared/alerts/enums/alertType";
import PageCard from "features/shared/components/card/PageCard";
import InfoItem from "features/shared/components/infoItem/InfoItem";
import IContentDialogService from "features/shared/components/contentDialog/IContentDialogService";
import ContentDialogConfirm from "features/shared/components/contentDialog/ContentDialogConfirm";
import ContractingNoteSubDialog from "features/contracting/components/ContractingNoteSubDialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { appendQueryParams, getForAdminParam, getQueryParams } from "features/shared/helpers/queryParams";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { trackPromise } from "react-promise-tracker";
import { useHistory } from "react-router-dom";
import { loadModule } from "features/shared/helpers/modules";
import ContractingStepConfiguration from "features/contracting/models/ContractingStepConfiguration";
import { createDefaultContracting } from "features/contracting/helpers/contractingFormHelper";
import { ContractingState } from "features/contracting/enums/contractingState";

const contractingSteps = loadModule<ContractingStepConfiguration[]>(
  "contracting/contractingSteps.json"
);

const editRoutePrefix = "/contracting/add";
const adminContractingsRoute = "/admin/contractings";

interface Props {
  contractingId: string;
  onVisibilityChange?: (visible: boolean) => void;
}

const ContractingAdminActions = ({ contractingId, onVisibilityChange }: Props) => {
  // translations
  const tLabels = useTranslation("contracting\\contracting").t;

  // DI
  const repo = useInjection<IRepository<object>>(IocTypes.Repository);
  const dialogService = useInjection<IContentDialogService>(
    IocTypes.ContentDialogService
  );

  // router
  const history = useHistory();

  // state
  const [reload, setReload] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const adminRecordQuery: DataQuery = {
    url: Contracting_List,
    params: appendQueryParams(
      getQueryParams("ContractingId", contractingId),
      "ForAdminUse",
      "true"
    ),
  };
  const { data: adminRecordPage } = useQuery<
    PagedDataResultDTO<ContractingSimplifiedDTO>
  >(adminRecordQuery, { result: [], count: 0 }, [contractingId, reload], {
    useTracker: true,
  });

  const adminRecord = adminRecordPage.result[0];
  const isWaitingForApproval =
    adminRecord?.contractingState ===
    ContractingState.WaitingForApprovalAfterCustomerSign;

  // side-effects
  useEffect(() => {
    onVisibilityChange?.(isWaitingForApproval);
  }, [isWaitingForApproval]);

  // helpers
  const getWarningCode = (response: any): string | undefined => {
    return (response?.data as ActionResultDTO<string>)?.validationMessages?.[0]
      ?.message;
  };

  const openErrorDialog = () => {
    setErrorMessage(tLabels("contractingAdminActions.decisionNotAllowedMessage"));
  };

  const postAdminAction = async (url: string, data: object) => {
    return await trackPromise(
      repo.post<ActionResultDTO<string>>({
        url: url,
        data: data,
        routeParams: getRouteParams("contractingId", contractingId),
        params: getForAdminParam(true),
      })
    );
  };

  const openConfirm = (
    titleKey: string,
    messageKey: string,
    action: () => void
  ) => {
    dialogService.openDialog(tLabels(titleKey), () => (
      <ContentDialogConfirm message={tLabels(messageKey)} action={action} />
    ));
  };

  // handlers
  const handleNoteSave = async (note: string) => {
    setErrorMessage(null);
    const response = await postAdminAction(Contracting_Note, { note: note });

    if (isSuccessStatus(response)) {
      setReload((k) => k + 1);
    } else {
      openErrorDialog();
    }
  };

  const handleApprove = async () => {
    setErrorMessage(null);
    const response = await postAdminAction(Contracting_Approve, {});
    const warning = getWarningCode(response);

    if (!isSuccessStatus(response) || warning) {
      openErrorDialog();
    }

    setReload((k) => k + 1);
  };

  const handleCancel = async () => {
    setErrorMessage(null);
    const response = await postAdminAction(Contracting_Cancel, {});
    const warning = getWarningCode(response);

    if (isSuccessStatus(response) && !warning) {
      history.push(adminContractingsRoute);
    } else {
      openErrorDialog();
      setReload((k) => k + 1);
    }
  };

  const handleEdit = async () => {
    const actualStepRoute = createDefaultContracting(contractingSteps, {
      routePrefix: editRoutePrefix,
      step: "paymentMethod",
    }).actualStepRoute;

    setErrorMessage(null);
    const response = await postAdminAction(Contracting_Edit, {
      actualStepRoute: actualStepRoute,
    });
    const warning = getWarningCode(response);
    const newContractingId = (response?.data as ActionResultDTO<string>)?.result;

    if (isSuccessStatus(response) && !warning && newContractingId) {
      history.push({
        pathname: editRoutePrefix,
        search: getQueryParams("contractingId", newContractingId).toString(),
      });
    } else {
      openErrorDialog();
      setReload((k) => k + 1);
    }
  };

  const openNoteDialog = () => {
    dialogService.openDialog(
      tLabels("contractingAdminActions.editNoteTitle"),
      () => (
        <div className="p-4">
          <ContractingNoteSubDialog
            initialNote={adminRecord?.note ?? ""}
            onSave={(note) => {
              handleNoteSave(note);
              dialogService.closeDialog();
            }}
            onCancel={() => dialogService.closeDialog()}
          />
        </div>
      )
    );
  };

  // sub renders
  const renderActionButtons = (): JSX.Element => {
    return (
      <div className="d-flex flex-wrap mt-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-success mr-2 mb-1"
          onClick={() =>
            openConfirm(
              "contractingAdminActions.approveConfirmTitle",
              "contractingAdminActions.approveConfirmMessage",
              handleApprove
            )
          }
        >
          <FontAwesomeIcon icon="check" />
          <span className="ml-1">
            {tLabels("contractingAdminActions.approve")}
          </span>
        </button>

        <button
          type="button"
          className="btn btn-sm btn-outline-info mr-2 mb-1"
          onClick={() =>
            openConfirm(
              "contractingAdminActions.editConfirmTitle",
              "contractingAdminActions.editConfirmMessage",
              handleEdit
            )
          }
        >
          <FontAwesomeIcon icon="edit" />
          <span className="ml-1">
            {tLabels("contractingAdminActions.edit")}
          </span>
        </button>

        <button
          type="button"
          className="btn btn-sm btn-outline-danger mb-1"
          onClick={() =>
            openConfirm(
              "contractingAdminActions.cancelConfirmTitle",
              "contractingAdminActions.cancelConfirmMessage",
              handleCancel
            )
          }
        >
          <FontAwesomeIcon icon="times" />
          <span className="ml-1">
            {tLabels("contractingAdminActions.cancel")}
          </span>
        </button>
      </div>
    );
  };

  const renderNote = (): JSX.Element => {
    if (!adminRecord?.note?.trim()) {
      return (
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary mt-2"
          onClick={openNoteDialog}
        >
          <FontAwesomeIcon icon="edit" />
          <span className="ml-1">
            {tLabels("contractingAdminActions.addNote")}
          </span>
        </button>
      );
    }

    return (
      <div className="d-flex">
        <span className="font-weight-bold text-break">{adminRecord.note}</span>
        <span
          className="text-secondary ml-1"
          role="button"
          onClick={openNoteDialog}
        >
          <FontAwesomeIcon icon="edit" />
        </span>
      </div>
    );
  };

  // render
  if (!isWaitingForApproval) {
    return null;
  }

  return (
    <>
      <div className="mb-3">
        <AlertWithButton
          alertInfo={`${tLabels(
            "contractingAdminActions.approvalPanelTitle"
          )} ${tLabels("contractingAdminActions.approvalPanelMessage")}`}
        />
      </div>

      {errorMessage && (
        <div className="mb-3">
          <AlertWithButton alertInfo={errorMessage} alertType={AlertType.Danger} />
        </div>
      )}

      <PageCard
        title={tLabels("contractingAdminActions.title")}
        icon="cog"
      >
        <div className="row no-gutters">
          <div className="col-auto px-sm-3">
            <InfoItem
              title={tLabels("contractingAdminActions.actions")}
              value={renderActionButtons()}
              valueBold={false}
            />
          </div>
          <div className="col-auto px-sm-3">
            <InfoItem
              title={tLabels("contractingAdminActions.note")}
              value={renderNote()}
              valueBold={false}
            />
          </div>
        </div>
      </PageCard>
    </>
  );
};

export default ContractingAdminActions;
